import { errorView, type ErrorView } from './queryView'
import type { ResponseDecoder } from './transport'

/**
 * Расширение карточки источника (ADR-21, RP-FB71): всё, что у карточки вида сверх общего ядра. Конверт один для всех видов;
 * содержимое `payload` общий код не читает и по нему не ветвится — его разбирает только описание вида в его пакете
 * (`extensionViewOf`). Здесь живёт только то, что одинаково у всех: проверка формы конверта и построение вида по описанию полей.
 */
export interface ProductExtensionEnvelope {
  kind: string
  schemaVersion: number
  /** Отпечаток содержимого `payload`: меняется, только когда изменились сами данные. */
  revision: string
  /** Когда служба прочитала карточку; `null` — служба не сообщила. */
  observedAt: string | null
  payload: Readonly<Record<string, unknown>>
}

function fail(message: string): never {
  throw new Error(message)
}

/**
 * Декодер ответа `GET …/catalog/products/{key}/extension`. Конверт другого вида — ошибка `invalid-response`, а не чужие данные
 * под видом данных этого источника.
 */
export function decodeProductExtensionEnvelope(expectedKind: string): ResponseDecoder<ProductExtensionEnvelope> {
  return (raw) => {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return fail('расширение карточки — не объект')
    const item = raw as Record<string, unknown>
    const { kind, schemaVersion, revision, observedAt, payload } = item
    if (typeof kind !== 'string') return fail('поле kind — не строка')
    if (kind !== expectedKind) return fail(`расширение вида «${kind}» пришло в запрос вида «${expectedKind}»`)
    if (typeof schemaVersion !== 'number' || !Number.isInteger(schemaVersion) || schemaVersion < 1) {
      return fail('поле schemaVersion — не целое число от 1')
    }
    if (typeof revision !== 'string') return fail('поле revision — не строка')
    if (observedAt !== undefined && observedAt !== null && typeof observedAt !== 'string') return fail('поле observedAt — не строка')
    if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) return fail('поле payload — не объект')
    return {
      kind,
      schemaVersion,
      revision,
      observedAt: typeof observedAt === 'string' ? observedAt : null,
      payload: payload as Record<string, unknown>,
    }
  }
}

/** Одно поле расширения для показа. */
export interface ExtensionField {
  id: string
  label: string
  valueText: string
}

/**
 * Расширение глазами экрана. `newer-schema` — служба отдала схему новее, чем знает клиент: данные целы, но показать их
 * здесь нельзя. `invalid` — данные не сходятся с описанием вида.
 */
export type ExtensionView =
  | { state: 'ok'; summary: string; fields: readonly ExtensionField[] }
  | { state: 'newer-schema'; supported: readonly number[]; got: number }
  | { state: 'invalid'; reason: string }

/** Одно поле описания вида: как называется и как его значение превращается в текст. */
export interface ExtensionFieldSpec {
  id: string
  label: string
  /** Входит ли поле в короткую сводку (остальные — только в «Все поля источника»). */
  inSummary: boolean
  /** Значение в текст; `null` — поля нет (не показывать). Неожиданный тип — бросить `Error` с причиной. */
  format: (value: unknown) => string | null
}

/** Описание полей вида по версиям схемы. */
export interface ExtensionSpec {
  versions: Readonly<Record<number, readonly ExtensionFieldSpec[]>>
}

const isMissing = (value: unknown): boolean => value === undefined || value === null

/** Текст: непустая строка или число. */
export function formatText(value: unknown): string | null {
  if (isMissing(value)) return null
  if (typeof value === 'string') return value.trim() === '' ? null : value
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  throw new Error('ожидался текст')
}

/** Логическое значение словами. */
export function formatFlag(whenTrue: string, whenFalse: string): (value: unknown) => string | null {
  return (value) => {
    if (isMissing(value)) return null
    if (typeof value === 'boolean') return value ? whenTrue : whenFalse
    throw new Error('ожидалось логическое значение')
  }
}

/**
 * Ссылка на другую запись: есть значение — `whenSet`, служба явно дала `null` — `whenEmpty`; саму ссылку человеку не показываем.
 * Ключа нет совсем — служба поле не читала, и «пусто» о нём говорить нельзя: поле не показывается.
 */
export function formatReference(whenSet: string, whenEmpty: string, emptyValues: readonly string[] = []): (value: unknown) => string | null {
  return (value) => {
    if (value === undefined) return null
    if (value === null) return whenEmpty
    if (typeof value !== 'string') throw new Error('ожидалась ссылка строкой')
    return value.trim() === '' || emptyValues.includes(value.trim()) ? whenEmpty : whenSet
  }
}

/** Число элементов списка или объекта: «3 свойства» собирает `unitText`. */
export function formatCount(unitText: (count: number) => string): (value: unknown) => string | null {
  return (value) => {
    if (isMissing(value)) return null
    if (Array.isArray(value)) return unitText(value.length)
    if (typeof value === 'object') return unitText(Object.keys(value as object).length)
    throw new Error('ожидался список или набор значений')
  }
}

/**
 * Вид расширения по описанию полей вида. Версия новее известных — `newer-schema`; версия старше, но неизвестная, и любое поле
 * неожиданного типа — `invalid` с причиной. Поля, которых в данных нет, не показываются: отсутствие не выдаётся за пустое значение.
 */
export function extensionViewOf(envelope: ProductExtensionEnvelope, spec: ExtensionSpec): ExtensionView {
  const known = Object.keys(spec.versions).map(Number).sort((left, right) => left - right)
  const fieldSpecs = spec.versions[envelope.schemaVersion]
  if (fieldSpecs === undefined) {
    const newest = known[known.length - 1] ?? 0
    return envelope.schemaVersion > newest
      ? { state: 'newer-schema', supported: known, got: envelope.schemaVersion }
      : { state: 'invalid', reason: `версия схемы ${envelope.schemaVersion} клиент не читает` }
  }
  const fields: ExtensionField[] = []
  const summary: string[] = []
  for (const field of fieldSpecs) {
    let valueText: string | null
    try {
      valueText = field.format(envelope.payload[field.id])
    } catch (error) {
      return { state: 'invalid', reason: `поле «${field.label}»: ${error instanceof Error ? error.message : 'неожиданное значение'}` }
    }
    if (valueText === null) continue
    fields.push({ id: field.id, label: field.label, valueText })
    if (field.inSummary) summary.push(`${field.label.toLowerCase()} ${valueText}`)
  }
  return { state: 'ok', summary: summary.join(' · '), fields }
}

/** То, что описание вида отдаёт общей карточке: ход чтения, ошибка и готовый вид. */
export interface ProductExtensionState {
  loading: boolean
  error: ErrorView | null
  view: ExtensionView | null
  /** Прочитать расширение ещё раз (кнопка «Повторить»). */
  reload: () => void
}

/** Минимум полей результата RTK Query, нужный для состояния расширения. */
export interface ProductExtensionResultLike {
  data?: ProductExtensionEnvelope | undefined
  error?: unknown
  isFetching: boolean
  refetch: () => unknown
}

/** Состояние расширения из результата запроса: ошибка даже при старых данных показывается ошибкой (F06). */
export function productExtensionStateOf(result: ProductExtensionResultLike, spec: ExtensionSpec): ProductExtensionState {
  const reload = () => {
    void result.refetch()
  }
  if (result.error !== undefined) return { loading: false, error: errorView(result.error), view: null, reload }
  if (result.data === undefined) return { loading: true, error: null, view: null, reload }
  return { loading: result.isFetching, error: null, view: extensionViewOf(result.data, spec), reload }
}
